import { useEffect, useState } from 'react';

/**
 * Hero background with a graceful loading state.
 *
 * The landing hero used CSS `background-image` divs over a flat maroon
 * header — so while the photo was still downloading you saw a flat
 * colour block (no placeholder, no fade). This renders instead:
 *
 *   1. A branded maroon gradient that's ALWAYS visible, so the hero reads
 *      as a designed surface the instant the page paints — never a flat
 *      flash while the image decodes.
 *   2. Real <img> layers that fade in only once each has actually loaded
 *      (onLoad), so the photo eases in over the gradient instead of
 *      popping. The first image loads eagerly + high priority.
 *   3. With more than one image, it crossfades through them on a timer.
 *
 * All layers are absolutely positioned; the consuming hero keeps its
 * content in a `relative z-10` wrapper above these.
 */
export function HeroBackground({ images }: { images: string[] }) {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setIndex(0);
    if (images.length < 2) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % images.length),
      6000,
    );
    return () => clearInterval(id);
  }, [images.length]);

  const active = images.length ? index % images.length : 0;

  return (
    <>
      {/* Always-on branded placeholder so there's never a flat-colour flash. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-br from-[#8e2a2f] via-[#7a2428] to-[#5e171b]"
      />
      {images.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt=""
          aria-hidden
          loading={i === 0 ? 'eager' : 'lazy'}
          // @ts-expect-error fetchpriority is a valid HTML attr; React's
          // DOM types lag, but it tells the browser to grab the hero first.
          fetchpriority={i === 0 ? 'high' : 'auto'}
          decoding="async"
          onLoad={() => setLoaded((m) => ({ ...m, [i]: true }))}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === active && loaded[i] ? 1 : 0 }}
        />
      ))}
      {/* Darkening overlay for text contrast. */}
      <div aria-hidden className="absolute inset-0 bg-black/20" />
    </>
  );
}
